import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Play, HelpCircle, FileText, Layers, AlignLeft,
  Move, GitMerge, Image, Headphones, Download, Edit3, Type,
  Plus, X, MessageCircle, Clock, MapPin, ArrowUpDown, GitBranch, ShieldCheck,
} from 'lucide-react';

const ELEMENT_TYPES = [
  { type: 'video', name: 'Video', icon: Play, color: 'bg-red-100 text-red-700' },
  { type: 'quiz', name: 'Quiz', icon: HelpCircle, color: 'bg-purple-100 text-purple-700' },
  { type: 'case_study', name: 'Case Study', icon: FileText, color: 'bg-blue-100 text-blue-700' },
  { type: 'flashcard', name: 'Flashcards', icon: Layers, color: 'bg-amber-100 text-amber-700' },
  { type: 'fill_gaps', name: 'Fill the Gaps', icon: AlignLeft, color: 'bg-green-100 text-green-700' },
  { type: 'drag_drop_task', name: 'Drag & Drop', icon: Move, color: 'bg-cyan-100 text-cyan-700' },
  { type: 'matching', name: 'Matching', icon: GitMerge, color: 'bg-pink-100 text-pink-700' },
  { type: 'image_hotspot', name: 'Image Hotspot', icon: Image, color: 'bg-indigo-100 text-indigo-700' },
  { type: 'audio', name: 'Audio', icon: Headphones, color: 'bg-orange-100 text-orange-700' },
  { type: 'downloadable', name: 'Download', icon: Download, color: 'bg-slate-100 text-slate-700' },
  { type: 'reflection_journal', name: 'Reflection', icon: Edit3, color: 'bg-teal-100 text-teal-700' },
  { type: 'text', name: 'Text', icon: Type, color: 'bg-gray-100 text-gray-700' },
  { type: 'trainer_tip', name: 'Trainer Tip', icon: MessageCircle, color: 'bg-emerald-100 text-emerald-700' },
  { type: 'timeline', name: 'Timeline', icon: Clock, color: 'bg-indigo-100 text-indigo-700' },
  { type: 'label_diagram', name: 'Label Diagram', icon: MapPin, color: 'bg-rose-100 text-rose-700' },
  { type: 'sort_order', name: 'Sort Order', icon: ArrowUpDown, color: 'bg-cyan-100 text-cyan-700' },
  { type: 'decision_tree', name: 'Decision Tree', icon: GitBranch, color: 'bg-violet-100 text-violet-700' },
  { type: 'progress_checkpoint', name: 'Checkpoint', icon: ShieldCheck, color: 'bg-amber-100 text-amber-700' },
];

const ElementSidebar = ({ onAddElement }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleAdd = (type) => {
    onAddElement(type);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:flex w-64 border-r border-slate-200 bg-white flex-col" data-testid="element-sidebar">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Elements</h3>
          <p className="text-xs text-slate-500 mt-1">Tap to add to selected module</p>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-2">
            {ELEMENT_TYPES.map((element) => {
              const Icon = element.icon;
              return (
                <button
                  key={element.type}
                  onClick={() => handleAdd(element.type)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-[#20acf6] hover:bg-slate-50 transition-all group min-h-[44px]"
                  data-testid={`add-element-${element.type}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${element.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-slate-900 group-hover:text-[#20acf6]">{element.name}</span>
                </button>
              );
            })}
          </div>
        </ScrollArea>
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-500 text-center">Tap elements to reorder within modules</p>
        </div>
      </div>

      {/* Mobile FAB + bottom sheet */}
      <div className="lg:hidden">
        <button
          onClick={() => setMobileOpen(true)}
          className="fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-[#20acf6] text-white shadow-lg shadow-[#20acf6]/30 flex items-center justify-center"
          data-testid="mobile-add-element-fab"
          aria-label="Add element"
        >
          <Plus className="h-6 w-6" />
        </button>

        {mobileOpen && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setMobileOpen(false)} />
            <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[70vh] flex flex-col" data-testid="mobile-element-sheet">
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">Add Element</h3>
                <button onClick={() => setMobileOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100">
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>
              <div className="overflow-y-auto p-3 grid grid-cols-3 gap-2">
                {ELEMENT_TYPES.map((element) => {
                  const Icon = element.icon;
                  return (
                    <button
                      key={element.type}
                      onClick={() => handleAdd(element.type)}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-[#20acf6] hover:bg-slate-50 min-h-[72px]"
                      data-testid={`mobile-add-${element.type}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${element.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-[10px] font-medium text-slate-700 text-center leading-tight">{element.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ElementSidebar;
